import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Menu, X, ArrowUpRight, ChevronDown, ArrowRight,
  ShieldCheck, Building2, Search, Users, Briefcase, Scale, BookOpen, FileText,
  Sparkles, HelpCircle, LifeBuoy,
} from "lucide-react";
import { HID } from "@/constants/cosecIds";
import { trackEvent, GA_EVENTS } from "@/lib/analytics";

const LOGO_BLUE = "/assets/cosec-logo-blue.png";
const LOGO_WHITE = "/assets/cosec-logo-white.png";

// Routes whose hero is dark, the header should start with the white logo + transparent bg
// over those, and swap to the standard blue logo + white bg as soon as the user scrolls.
const DARK_HERO_ROUTES = new Set(["/", "/coverify", "/cosearch", "/coform", "/ai", "/cosecapp", "/company-secretaries", "/accountants", "/corporate-law"]);

/* ============================================================
   MEGA-MENU DATA. Clio-style multi-column nav
   ============================================================ */
const MENU = {
  products: {
    label: "Products",
    columns: [
      {
        heading: "Software",
        items: [
          { to: "/cosecapp", Icon: Building2, t: "CoSec App",
            d: "AI-powered entity & company-secretarial management for UK teams." },
          { to: "/coform", Icon: FileText, t: "CoForm",
            d: "Form your UK company in under an hour, authorised Companies House agent." },
        ],
      },
      {
        heading: "Services & APIs",
        items: [
          { to: "/coverify", Icon: ShieldCheck, t: "CoVerify",
            badge: "Live",
            d: "Connected ID verification & KYC. ECCTA-ready." },
          { to: "/cosearch", Icon: Search, t: "CoSearch",
            badge: "New",
            d: "UK company intelligence, 5m+ companies, instantly." },
        ],
      },
    ],
    featured: {
      eyebrow: "Spotlight",
      to: "/coverify",
      title: "CoVerify is live",
      copy: "Run a real ID verification right now, facial match + document liveness in under 60 seconds.",
      cta: "Try the demo",
      tone: "accent",
    },
  },
  solutions: {
    label: "Solutions",
    columns: [
      {
        heading: "By role",
        items: [
          { to: "/company-secretaries", Icon: Briefcase, t: "Company Secretaries",
            d: "For UK in-house Cosec teams managing 50+ entities." },
          { to: "/accountants", Icon: Users, t: "Accountants",
            d: "Deliver Cosec as a service line, branded for your firm." },
          { to: "/corporate-law", Icon: Scale, t: "Corporate Law",
            d: "Group structures, beneficial ownership, audit trails." },
        ],
      },
      {
        heading: "By workflow",
        items: [
          { to: "/services", Icon: FileText, t: "All services",
            d: "Confirmation statements, share allotments, PSC filings." },
          { to: "/company-secretarial-services", Icon: Briefcase, t: "Outsourced Cosec",
            d: "We run your Cosec function end-to-end." },
          { to: "/company-formation", Icon: Building2, t: "Company formation",
            d: "Incorporate UK companies in under an hour." },
        ],
      },
    ],
    featured: {
      eyebrow: "Case study",
      to: "/case-studies",
      title: "How UK firms cut Cosec admin by 70%",
      copy: "See how Veridian Legal and Northgate Group moved 240 entities onto CoSec App.",
      cta: "Read the stories",
      tone: "primary",
    },
  },
  resources: {
    label: "Resources",
    columns: [
      {
        heading: "Library",
        items: [
          { to: "/blog", Icon: BookOpen, t: "Blog",
            d: "Compliance & Cosec insights, updated weekly." },
          { to: "/case-studies", Icon: FileText, t: "Case studies",
            d: "How UK teams use CoSec App, CoVerify and CoSearch." },
          { to: "/guides", Icon: FileText, t: "Guides",
            d: "Long-form playbooks for UK statutory compliance." },
          { to: "/glossary", Icon: BookOpen, t: "Glossary",
            d: "Plain-English definitions for Cosec terminology." },
        ],
      },
      {
        heading: "Help",
        items: [
          { to: "/kb", Icon: BookOpen, t: "Knowledge Base",
            d: "How-tos, product walkthroughs and help articles, searchable." },
          { to: "/faqs", Icon: HelpCircle, t: "FAQs",
            d: "Common questions answered across all products." },
          { to: "/partners", Icon: Users, t: "Partners",
            d: "Our accountancy, law-firm and reseller network." },
          { to: "/contact", Icon: LifeBuoy, t: "Talk to a human",
            d: "Speak to our team about your specific use case." },
        ],
      },
    ],
    featured: {
      eyebrow: "Featured",
      to: "/blog",
      title: "ECCT Act 2023, the practical guide",
      copy: "Identity verification, ACSP licences and the new register of overseas entities, demystified.",
      cta: "Read on the blog",
      tone: "muted",
    },
  },
};

/* ============================================================
   SHARED MEGA-MENU PANEL, single panel rendered at the nav row
   level so it centers on the page rather than on a button.
   ============================================================ */
function SharedMegaPanel({ menuKey, onMouseEnter, onMouseLeave, onClose }) {
  if (!menuKey) return null;
  const data = MENU[menuKey];
  if (!data) return null;

  const tone = data.featured.tone || "primary";
  const toneClass =
    tone === "accent"
      ? "bg-accent text-primary"
      : tone === "muted"
      ? "bg-secondary text-primary"
      : "bg-primary text-primary-foreground";
  const toneCta =
    tone === "accent"
      ? "text-primary"
      : tone === "muted"
      ? "text-primary"
      : "text-accent";

  return (
    <div
      data-testid={`mega-panel-${menuKey}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[min(960px,calc(100vw-32px))] rounded-2xl border border-border bg-white shadow-[0_24px_60px_rgba(14,58,95,0.18)] p-2 z-50"
    >
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
          {data.columns.map((col) => (
            <div key={col.heading}>
              <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                {col.heading}
              </div>
              <div className="space-y-1">
                {col.items.map((it) => (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={onClose}
                    data-testid={`mega-link-${it.to.replace(/\//g, "-")}`}
                    className="group flex items-start gap-3 rounded-xl p-3 hover:bg-secondary transition-colors"
                  >
                    <div className="h-10 w-10 grid place-items-center rounded-lg bg-primary/8 text-primary group-hover:bg-accent group-hover:text-primary transition-colors shrink-0">
                      <it.Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-primary text-sm leading-tight flex items-center gap-2">
                        {it.t}
                        {it.badge && (
                          <span className="inline-block text-[9px] font-bold uppercase tracking-wider rounded-full bg-accent/25 text-primary px-1.5 py-0.5">
                            {it.badge}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">
                        {it.d}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Link
          to={data.featured.to}
          onClick={onClose}
          data-testid={`mega-featured-${menuKey}`}
          className={`col-span-12 lg:col-span-4 m-2 rounded-xl p-6 flex flex-col justify-between min-h-[220px] overflow-hidden relative ${toneClass}`}
        >
          <div className="absolute inset-0 bg-dotgrid opacity-[0.06] pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] opacity-80">
              <Sparkles className="h-3 w-3" /> {data.featured.eyebrow}
            </div>
            <div className="mt-3 font-display text-lg font-bold leading-tight">
              {data.featured.title}
            </div>
            <p className="mt-2 text-xs opacity-80 leading-relaxed">
              {data.featured.copy}
            </p>
          </div>
          <div className={`relative mt-4 inline-flex items-center gap-1 text-xs font-bold ${toneCta}`}>
            {data.featured.cta} <ArrowRight className="h-3.5 w-3.5" />
          </div>
        </Link>
      </div>
    </div>
  );
}

/* ============================================================
   HEADER (default export)
   - Shared mega-menu panel anchored to nav row (centered on page)
   - Simple flat mobile drawer
   ============================================================ */

// Static, declared outside component so React doesn't see a new type each render.
const MOBILE_LINKS = [
  { to: "/cosecapp", t: "CoSec App" },
  { to: "/coverify", t: "CoVerify", badge: "Live" },
  { to: "/cosearch", t: "CoSearch", badge: "New" },
  { to: "/coform", t: "CoForm" },
  { to: "/services", t: "Services" },
  { to: "/pricing", t: "Pricing" },
  { to: "/blog", t: "Blog" },
  { to: "/case-studies", t: "Case studies" },
  { to: "/kb", t: "Knowledge Base" },
  { to: "/about", t: "About" },
  { to: "/contact", t: "Contact" },
];

function NavBtn({ menuKey, testid, openKey, openMenu, scheduleClose, setOpenKey }) {
  const active = openKey === menuKey;
  return (
    <button
      data-testid={testid}
      onMouseEnter={() => openMenu(menuKey)}
      onMouseLeave={scheduleClose}
      onClick={() => setOpenKey(active ? null : menuKey)}
      className={`px-3 py-2 text-sm font-medium rounded-md inline-flex items-center gap-1 transition-colors ${
        active ? "text-primary bg-secondary" : "text-primary/80 hover:text-primary hover:bg-secondary"
      }`}
      aria-expanded={active}
    >
      {MENU[menuKey].label}
      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${active ? "rotate-180" : ""}`} />
    </button>
  );
}

export default function CosecHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const { pathname } = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenKey(null);
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpenKey(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openMenu = (k) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenKey(k);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenKey(null), 140);
  };
  const closeMobile = () => setMobileOpen(false);

  const onDarkHero = false; // always use the standard white header to avoid white-on-white edge cases
  const onDarkSurface = false;

  const linkBase = onDarkSurface
    ? "px-3 py-2 text-sm font-medium text-white/85 hover:text-white rounded-md hover:bg-white/10 transition-colors"
    : "px-3 py-2 text-sm font-medium text-primary/80 hover:text-primary rounded-md hover:bg-secondary transition-colors";
  const isActive = (p) => (pathname === p ? (onDarkSurface ? "text-white bg-white/10" : "text-primary bg-secondary") : "");

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-border shadow-[0_1px_0_rgba(14,58,95,0.04)]"
          : onDarkHero
          ? "bg-transparent"
          : "bg-white/80 backdrop-blur-sm"
      }`}
      onMouseLeave={scheduleClose}
    >
      <div className="container-cv flex h-16 md:h-20 items-center justify-between gap-4 relative">
        <Link to="/" data-testid={HID.headerLogo} className="flex items-center gap-3 shrink-0">
          <img
            src={onDarkSurface ? LOGO_WHITE : LOGO_BLUE}
            alt="CoSec Global"
            className="h-9 md:h-10 w-auto transition-opacity duration-200"
          />
        </Link>

        {/* Desktop nav, wrapped so MegaPanel can anchor to its left:50% */}
        <nav
          className="hidden lg:flex items-center gap-1 relative"
          onMouseEnter={() => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } }}
        >
          <NavBtn menuKey="products" testid={HID.navProducts} openKey={openKey} openMenu={openMenu} scheduleClose={scheduleClose} setOpenKey={setOpenKey} />
          <NavBtn menuKey="solutions" testid={HID.navFeatures} openKey={openKey} openMenu={openMenu} scheduleClose={scheduleClose} setOpenKey={setOpenKey} />
          <Link to="/pricing" className={`${linkBase} ${isActive("/pricing")}`}>Pricing</Link>
          <NavBtn menuKey="resources" testid="nav-resources" openKey={openKey} openMenu={openMenu} scheduleClose={scheduleClose} setOpenKey={setOpenKey} />
          <Link to="/about" data-testid={HID.navSecurity} className={`${linkBase} ${isActive("/about")}`}>About</Link>
          <Link to="/contact" data-testid={HID.navFaqs} className={`${linkBase} ${isActive("/contact")}`}>Contact</Link>

          {/* Shared panel, centered on nav row, not on a button */}
          <SharedMegaPanel
            menuKey={openKey}
            onMouseEnter={() => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } }}
            onMouseLeave={scheduleClose}
            onClose={() => setOpenKey(null)}
          />
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="https://www.cosec.app/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-login"
            className="hidden sm:inline-flex items-center gap-1 h-10 px-4 rounded-md border border-primary/20 text-primary text-sm font-semibold hover:bg-secondary hover:border-primary/40 transition-colors"
          >
            Login <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
          <Button asChild data-testid={HID.navBookDemo} className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
            <Link to="/contact" onClick={() => { try { trackEvent(GA_EVENTS.BOOK_DEMO_CLICK, { source: "header" }); } catch (e) { /* noop */ } }}>
              Book a Demo <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
          <button
            data-testid={HID.mobileToggle}
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-border"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Simple flat mobile drawer (no accordions) */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-white max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="container-cv py-3 flex flex-col gap-0.5">
            {MOBILE_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={closeMobile}
                className="flex items-center justify-between px-3 py-3 text-base font-medium text-primary/90 rounded-md hover:bg-secondary"
              >
                <span>{l.t}</span>
                {l.badge && (
                  <span className="text-[9px] font-bold uppercase tracking-wider rounded-full bg-accent/25 text-primary px-1.5 py-0.5">
                    {l.badge}
                  </span>
                )}
              </Link>
            ))}
            <a
              href="https://www.cosec.app/login"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-login"
              onClick={closeMobile}
              className="flex items-center justify-between px-3 py-3 text-base font-medium text-primary/90 rounded-md hover:bg-secondary"
            >
              <span>Login</span>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </a>
            <Link
              to="/contact"
              onClick={closeMobile}
              className="mt-3 mb-4 inline-flex w-full items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold px-4 py-3"
            >
              Book a Demo <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
